import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Image, View } from 'react-native'
import { useModalize } from 'react-native-modalize'

import { TokenResult } from '@ambire-common/libs/portfolio'
import { FormatType } from '@ambire-common/utils/formatDecimals/formatDecimals'
// @ts-ignore
import rewardsImage from '@common/assets/images/AmbireLogoLikeCoin.png'
import BatchIcon from '@common/assets/svg/BatchIcon'
import PendingToBeConfirmedIcon from '@common/assets/svg/PendingToBeConfirmedIcon'
import BottomSheet from '@common/components/BottomSheet'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import TokenIcon from '@common/components/TokenIcon'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import getAndFormatTokenDetails from '@common/modules/dashboard/helpers/getTokenDetails'
import { HIDDEN_BALANCE_MASK } from '@common/modules/dashboard/helpers/hiddenBalance'
import spacings, { SPACING_2XL, SPACING_TY } from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import { BORDER_RADIUS_PRIMARY } from '@common/styles/utils/common'
import flexboxStyles from '@common/styles/utils/flexbox'
import { AnimatedPressable, useCustomHover } from '@web/hooks/useHover'
import { getTokenId } from '@web/utils/token'
import useAppMode from '@web/hooks/useAppMode'
import { ModeEnum } from '@web/contexts/appModeContext'
import useRailgunContext from '@web/hooks/useRailgunContext'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import { getMissingPOIUsdByToken, priceKey } from '@web/utils/railgunTokens'
import { RailgunChainRowIcon, useRailgunChainStatus } from '@web/components/RailgunStatusBadge'

import TokenDetails from '../TokenDetails'
import PendingBadge from './PendingBadge'
import getStyles from './styles'
import { TokenRowContextProps } from './TokenItem'

type Props = TokenRowContextProps & {
  token: TokenResult
  extraActions?: React.ReactNode
  rewardsStyle?: boolean
  label?: string | React.ReactNode
  borderRadius?: number
  decimalRulesType?: FormatType
  hasBottomSpacing?: boolean
  onPress?: () => void
  wrapperTestID?: string
}

const BaseTokenItem = ({
  token,
  networks,
  simulatedAccountOp,
  balanceHidden = false,
  extraActions,
  rewardsStyle,
  label,
  borderRadius,
  decimalRulesType = 'amount',
  hasBottomSpacing = false,
  onPress,
  wrapperTestID
}: Props) => {
  // `networks` and `simulatedAccountOp` arrive as props on purpose. Subscribing
  // to the networks/selected-account controllers from inside a row makes
  // `React.memo` below useless: the portfolio object identity changes on every
  // controller update (and it emits once per network as balances stream in), so
  // every mounted row re-rendered ~20 times per refresh regardless of whether
  // its own token changed. The list reads both once and passes them down.
  const { t } = useTranslation()
  const { styles, theme, themeType } = useTheme(getStyles)
  const { mode } = useAppMode()
  const isPrivateMode = mode === ModeEnum.PRIVATE
  const { currentRailgunWallet } = useRailgunContext()

  // Phase 4.4 — per-token pending POI subscript (mirrors the per-chain version on the
  // Networks screen). Only relevant in private mode; for public mode this resolves to 0.
  const pendingPOIUsd = useMemo(() => {
    if (!isPrivateMode) return 0
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    if (!prices) return 0
    const byToken = getMissingPOIUsdByToken(
      currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
      currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
      prices
    )
    return byToken.get(priceKey(Number(token.chainId), token.address)) ?? 0
  }, [
    isPrivateMode,
    currentRailgunWallet?.balanceTokenPriceMap,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
    token.chainId,
    token.address
  ])

  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()

  // The details sheet is mounted only after the row is first pressed. It renders
  // a Portal + Modalize + the whole TokenDetails subtree, so mounting it eagerly
  // costs that tree once *per row* — on an account holding hundreds of tokens
  // that dominates both the DOM node count and the page's memory, for sheets the
  // user will never open. `pendingOpenRef` carries the very first press across
  // the mount, since `openBottomSheet()` is a no-op until Modalize exists.
  const [isSheetMounted, setIsSheetMounted] = useState(false)
  const pendingOpenRef = useRef(false)

  const handlePress = useCallback(() => {
    if (rewardsStyle && onPress) {
      onPress()
      return
    }
    if (!isSheetMounted) {
      pendingOpenRef.current = true
      setIsSheetMounted(true)
      return
    }
    openBottomSheet()
  }, [rewardsStyle, onPress, isSheetMounted, openBottomSheet])

  useEffect(() => {
    if (!isSheetMounted || !pendingOpenRef.current) return
    pendingOpenRef.current = false
    openBottomSheet()
  }, [isSheetMounted, openBottomSheet])

  const isDark = themeType === THEME_TYPES.DARK
  const hasRewardsStyle = Boolean(rewardsStyle)
  const getColors = useMemo(() => {
    if (!hasRewardsStyle) {
      return {
        from: 'transparent',
        to: isDark ? theme.tertiaryBackground : theme.secondaryBackground
      }
    }

    return {
      from: isDark ? theme.tertiaryBackground : theme.secondaryBackground,
      to: isDark ? theme.secondaryBackground : theme.tertiaryBackground
    }
  }, [
    hasRewardsStyle,
    isDark,
    theme.secondaryBackground,
    theme.tertiaryBackground,
    theme.primaryBackground
  ])

  const { from, to } = getColors

  const [bindAnim, animStyle, isHovered] = useCustomHover({
    property: 'backgroundColor',
    values: { from, to }
  })

  const tokenId = getTokenId(token)

  const {
    symbol,
    address,
    chainId,
    flags: { onGasTank }
  } = token

  // Formatting a token runs several `Intl.NumberFormat` passes; keep it out of
  // re-renders caused by hover/theme/sheet state, which don't change any input.
  const {
    balanceFormatted,
    balance,
    balanceLatestFormatted,
    priceUSDFormatted,
    balanceUSDFormatted,
    isPending: hasPendingBadges,
    pendingBalance,
    pendingBalanceFormatted,
    pendingBalanceUSDFormatted,
    pendingToBeSigned,
    pendingToBeSignedFormatted,
    pendingToBeConfirmed,
    pendingToBeConfirmedFormatted
  } = useMemo(
    () => getAndFormatTokenDetails(token, networks, simulatedAccountOp, { decimalRulesType }),
    [token, networks, simulatedAccountOp, decimalRulesType]
  )

  const isPending = !!hasPendingBadges

  // P3 — per-chain row status icon, only meaningful in private mode (where the
  // SW publishes a snapshot). `useRailgunChainStatus` returns null when feature
  // flag is off or when the chain isn't yet tracked, so the icon is fully
  // suppressed outside the new code path.
  const railgunChainStatus = useRailgunChainStatus(isPrivateMode ? chainId : undefined)

  const textColor = useMemo(() => {
    if (!isPending) return theme.primaryText
    return pendingToBeSigned ? theme.warningText : theme.info2Text
  }, [isPending, pendingToBeSigned, theme.primaryText, theme.warningText, theme.info2Text])

  return (
    <AnimatedPressable
      testID={wrapperTestID || undefined}
      onPress={handlePress}
      style={[
        styles.container,
        {
          borderRadius: borderRadius || BORDER_RADIUS_PRIMARY,
          marginBottom: hasBottomSpacing ? SPACING_TY : 0,
          ...(rewardsStyle && {
            boxShadow: `0 ${isHovered ? 2 : 3}px 0 0 ${String(theme.iconPrimary2)}`
          })
        },
        animStyle
      ]}
      {...bindAnim}
    >
      {isSheetMounted && (
        <BottomSheet
          id={`token-details-${address}`}
          sheetRef={sheetRef}
          closeBottomSheet={closeBottomSheet}
        >
          <TokenDetails
            token={token}
            handleClose={closeBottomSheet}
            isPrivateMode={isPrivateMode}
            balanceHidden={balanceHidden}
          />
        </BottomSheet>
      )}

      <View style={flexboxStyles.flex1}>
        <View
          style={[
            flexboxStyles.directionRow,
            flexboxStyles.flex1,
            flexboxStyles.alignCenter,
            flexboxStyles.justifySpaceBetween
          ]}
        >
          {/* LEFT — token icon (with network badge) + symbol over price */}
          <View
            style={[flexboxStyles.directionRow, flexboxStyles.alignCenter, flexboxStyles.flex1]}
          >
            <View style={[spacings.mr, flexboxStyles.justifyCenter]}>
              {rewardsStyle ? (
                <Image source={rewardsImage as any} style={{ width: 40, height: 40 }} />
              ) : (
                <TokenIcon
                  withContainer
                  address={address}
                  chainId={chainId}
                  onGasTank={onGasTank}
                  containerHeight={40}
                  containerWidth={40}
                  width={28}
                  height={28}
                />
              )}
            </View>

            <View style={[flexboxStyles.flex1, spacings.mr]}>
              <Text
                color={rewardsStyle ? theme.projectedRewards : textColor}
                fontSize={16}
                weight="number_bold"
                numberOfLines={1}
              >
                {symbol}
              </Text>
              <View style={[flexboxStyles.directionRow, flexboxStyles.alignCenter]}>
                <RailgunChainRowIcon
                  status={railgunChainStatus}
                  tooltipId={`${tokenId}-railgun-status`}
                />
                <Text appearance="secondaryText" fontSize={14} weight="regular" numberOfLines={1}>
                  {label ?? priceUSDFormatted}
                </Text>
              </View>
            </View>

            {/* area for optional actions (Claim button etc) */}
            {extraActions}
          </View>

          {/* RIGHT — token amount over USD value */}
          <View style={{ alignItems: 'flex-end', marginLeft: 16 }}>
            <Text
              selectable
              color={textColor}
              fontSize={16}
              weight="number_bold"
              numberOfLines={1}
              dataSet={createGlobalTooltipDataSet({
                id: `${tokenId}-balance`,
                // The tooltip carries the exact balance — empty it while hidden.
                content: balanceHidden ? '' : String(isPending ? pendingBalance : balance)
              })}
              testID={`token-balance-${tokenId}`}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {balanceHidden
                ? HIDDEN_BALANCE_MASK
                : isPending
                  ? pendingBalanceFormatted
                  : balanceFormatted}
            </Text>
            <Text selectable appearance="secondaryText" fontSize={14} weight="regular">
              {/* eslint-disable-next-line no-nested-ternary */}
              {balanceHidden
                ? HIDDEN_BALANCE_MASK
                : isPending
                  ? pendingBalanceUSDFormatted
                  : balanceUSDFormatted}
            </Text>
            {!balanceHidden && pendingPOIUsd > 0 && (
              <Text fontSize={11} color={theme.secondaryText} weight="regular">
                {t('+{{amount}} pending POI', { amount: formatDecimals(pendingPOIUsd, 'value') })}
              </Text>
            )}
          </View>
        </View>

        {isPending && !balanceHidden && (
          <View style={[{ marginLeft: SPACING_2XL + SPACING_TY }, spacings.mtSm]}>
            <View>
              {!!pendingToBeSigned && !!pendingToBeSignedFormatted && (
                <PendingBadge
                  amount={pendingToBeSigned}
                  amountFormatted={pendingToBeSignedFormatted}
                  label={t('awaiting signature')}
                  backgroundColor={theme.warningBackground}
                  textColor={theme.warningText}
                  Icon={BatchIcon}
                />
              )}
              {!!pendingToBeConfirmed && !!pendingToBeConfirmedFormatted && (
                <PendingBadge
                  amount={pendingToBeConfirmed}
                  amountFormatted={pendingToBeConfirmedFormatted}
                  label={t('confirming')}
                  backgroundColor={theme.info2Background}
                  textColor={theme.info2Text}
                  Icon={PendingToBeConfirmedIcon}
                />
              )}
            </View>

            <View style={[flexboxStyles.directionRow, flexboxStyles.alignCenter]}>
              <Text
                selectable
                style={[spacings.mrMi, { opacity: 0.7 }]}
                color={theme.successText}
                fontSize={14}
                weight="number_bold"
                numberOfLines={1}
              >
                {balanceLatestFormatted}
              </Text>
              <Text
                selectable
                style={{ opacity: 0.7 }}
                color={theme.successText}
                fontSize={12}
                numberOfLines={1}
              >
                {t('(Onchain)')}
              </Text>
            </View>
          </View>
        )}
      </View>
    </AnimatedPressable>
  )
}

export default React.memo(BaseTokenItem)
